/**
 * Floor Plan Viewer DOM Component
 *
 * Mobile-only full-screen "layout image + Back" screen shown when the user taps the "View Floor
 * Plan" button on floorPlanDetail's Configuration tab (see plugins/floorPlanDetail.plugin.js).
 * Owns its own markup and Back-button click handling — the plugin only supplies onBackClick and
 * calls setImage() with the plan to display, matching the createTabDom/createTabsDom pattern in
 * this same folder (own element, own event delegation, exposed update methods).
 *
 * @module nativeInteractiveSiteMap/components/floorPlanViewer.dom
 */
import { backButtonIconSvg } from 'constants/svg-assets';
import renderFloorPlanCarousel, { initializeFloorPlanCarousel } from './floorPlanCarousel.dom';

const ROOT_CLASS = 'nhs-u-flex-column nhs-u-height--full';
const BACK_BUTTON_CLASS = 'nhs-c-native-interactive-site-map__back-button';
const HEADER_CLASS = [
  'nhs-c-native-interactive-site-map__floorplan-detail-header',
  'nhs-mq-native-interactive-site-map__floorplan-detail-header-large',
].join(' ');
const TITLE_CLASS = [
  'nhs-c-native-interactive-site-map__panel-title',
  'nhs-mq-native-interactive-site-map__panel-title-medium',
  'nhs-o-typography--sans-body-1-medium-medium',
].join(' ');
const IMAGE_SLOT_CLASS = [
  'nhs-u-flex--1',
  'nhs-u-display--flex',
  'nhs-u-items-center',
  'nhs-u-justify-content-center',
].join(' ');

/**
 * @param {object} params
 * @param {Function} params.onBackClick - called when the Back button is clicked
 * @returns {{ getElement: Function, setImage: Function, remove: Function }}
 */
export default function createFloorPlanViewer({ onBackClick }) {
  const state = { root: null, imageSlot: null, clickHandler: null };

  function initializeDom() {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div class="${ROOT_CLASS}" data-qa="native_ism_floor_plan_viewer">
        <div class="${HEADER_CLASS}">
          <button type="button"
                  class="${BACK_BUTTON_CLASS}"
                  aria-label="Go back"
                  data-image-collapse-button
                  data-qa="native_ism_floor_plan_viewer_back_button">
            ${backButtonIconSvg}
          </button>
          <h4 class="${TITLE_CLASS}" data-qa="native_ism_floor_plan_viewer_title">Floor plan</h4>
        </div>
        <div class="${IMAGE_SLOT_CLASS}" data-image-slot></div>
      </div>
    `;
    state.root = wrapper.firstElementChild;
    state.imageSlot = state.root.querySelector('[data-image-slot]');
  }

  initializeDom();

  if (typeof onBackClick === 'function') {
    state.clickHandler = (event) => {
      if (event.target.closest('[data-image-collapse-button]')) {
        onBackClick();
      }
    };
    state.root.addEventListener('click', state.clickHandler);
  }

  return {
    getElement() {
      return state.root;
    },

    /**
     * @param {object|null} plan
     */
    setImage(plan) {
      state.imageSlot.innerHTML = renderFloorPlanCarousel({ floorplans: plan?.floorplans });
      initializeFloorPlanCarousel();
    },

    remove() {
      if (state.clickHandler) {
        state.root?.removeEventListener('click', state.clickHandler);
        state.clickHandler = null;
      }
      state.root?.remove();
      state.root = null;
      state.imageSlot = null;
    },
  };
}
